/**
 * Guard React against DOM-mutating browser features — above all Chrome's
 * auto-translate, which wraps every text node in <font> tags and detaches
 * the nodes React owns. React's commit phase then throws
 * `NotFoundError: Failed to execute 'removeChild' on 'Node'` (or the
 * insertBefore twin) and the whole admin crashes to the error boundary.
 *
 * This is exactly what non-English organizers hit in production: the
 * 2026-09-03 "truchas" onboarding session crashed SIX times across six
 * routes (Sentry issue "removeChildFromContainer", 27 events, users with
 * Spanish/Portuguese-translated UIs), each recovered only by "Reload page".
 *
 * The guard is the long-standing workaround from facebook/react#11538:
 * make the two operations tolerant when the child has been re-parented —
 * removeChild becomes a no-op, insertBefore falls back to appendChild.
 * Translation keeps working; React keeps running.
 *
 * Must be installed BEFORE ReactDOM renders (imported first in main.tsx).
 */
export function installDomTranslationGuard(): void {
  if (typeof Node !== 'function' || !Node.prototype) return;

  const originalRemoveChild = Node.prototype.removeChild;
  Node.prototype.removeChild = function removeChildGuarded<T extends Node>(
    this: Node,
    child: T,
  ): T {
    if (child.parentNode !== this) {
      return child;
    }
    return originalRemoveChild.call(this, child) as T;
  };

  const originalInsertBefore = Node.prototype.insertBefore;
  Node.prototype.insertBefore = function insertBeforeGuarded<T extends Node>(
    this: Node,
    newNode: T,
    referenceNode: Node | null,
  ): T {
    if (referenceNode && referenceNode.parentNode !== this) {
      return this.appendChild(newNode);
    }
    return originalInsertBefore.call(this, newNode, referenceNode) as T;
  };
}
